import React, { useEffect, useState } from 'react';
import { Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import usePermissions from '../../hooks/usePermissions';
import useStorePlan from '../../hooks/useStorePlan';
import { Permissions } from '../../constants/Permissions';
import ConsentFormService from '../../services/consentFormService';

/**
 * Tells staff booking or selling on a customer's behalf that some consent forms are still to be
 * accepted. It never blocks: the customer accepts later from the link the store sends.
 */
const PendingConsentAlert = ({
  id,
  storeId,
  customerId,
  serviceIds,
  serviceComboIds,
  servicePackageId,
  membershipPlanId,
  variant = 'booking',
  sx,
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions(storeId);
  const { entitlements } = useStorePlan(storeId, Boolean(storeId));
  const [pendingCount, setPendingCount] = useState(0);
  const enabled = Boolean(storeId && customerId)
    && hasPermission(Permissions.ConsentFormsRead)
    && Boolean(entitlements?.allowsConsentForms);
  const serviceIdsKey = (serviceIds || []).join(',');
  const serviceComboIdsKey = (serviceComboIds || []).join(',');

  useEffect(() => {
    const hasScope = serviceIdsKey || serviceComboIdsKey || servicePackageId || membershipPlanId;
    if (!enabled || !hasScope) {
      setPendingCount(0);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      ConsentFormService.getManagementRequirements(storeId, {
        customerId,
        serviceIds: serviceIdsKey ? serviceIdsKey.split(',') : [],
        serviceComboIds: serviceComboIdsKey ? serviceComboIdsKey.split(',') : [],
        servicePackageId,
        membershipPlanId,
      })
        .then((pending) => {
          if (!cancelled) {
            setPendingCount(pending.length);
          }
        })
        .catch((loadError) => {
          console.error('Failed to check consent forms:', loadError);
          if (!cancelled) {
            setPendingCount(0);
          }
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [customerId, enabled, membershipPlanId, serviceComboIdsKey, serviceIdsKey, servicePackageId, storeId]);

  if (!enabled || pendingCount === 0) {
    return null;
  }

  return (
    <Alert id={id} severity="info" sx={sx}>
      {t(variant === 'purchase' ? 'consentForms.staff.purchaseAlert' : 'consentForms.staff.bookingAlert', { count: pendingCount })}
    </Alert>
  );
};

export default PendingConsentAlert;
